<template>
  <div class="hstack justify-content-center position-relative">
    <div class="position-absolute align-center">
      <div v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <div
      class="d-flex toggle-group-wrapper p-0"
      :class="{ 'toggle-group-opacity-50': isGloballyDisabled, 'toggle-group-large': isLargeVariant }"
    >
      <label v-for="(option, index) in options" :key="index" class="p-0 w-100">
        <input
          v-model="selectedValue"
          type="radio"
          :value="index"
          class="toggle-group-input"
          :disabled="isOptionDisabled(option)"
        />
        <div class="toggle-group-value-container" v-html="option.label"></div>
      </label>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    options: {
      type: Array,
      required: true,
      validator: (options) => options.every((option) => typeof option.label === 'string'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: '',
      validator: (v) => ['', 'large'].includes(v),
    },
  },
  data() {
    return {
      selectedValue: null,
    };
  },
  computed: {
    isGloballyDisabled() {
      return this.disabled || this.isLoading;
    },
    isLargeVariant() {
      return this.variant === 'large';
    },
  },
  watch: {
    selectedValue(newValue) {
      this.$emit('change', {
        index: newValue,
        option: this.options[newValue],
      });
    },
    // Re-sync internal selection when the parent updates the `selected` flag
    // on options (e.g. Inbound auto-selects "By Last Stop Arrival", or LOOP
    // is reset to ALL when loop routes disappear). Without this, the radio
    // selection stays stale because `initializeSelection` only runs in
    // `created`. Vue won't trigger `selectedValue` watcher if the index is
    // unchanged, so no duplicate `change` event is emitted in that case.
    // `deep: true` covers both reference changes (current consumers use
    // `.map()` in computeds) and in-place `options[i].selected` mutations
    // that a future consumer might introduce.
    options: {
      deep: true,
      handler() {
        this.initializeSelection();
      },
    },
  },
  created() {
    this.initializeSelection();
  },
  methods: {
    isOptionDisabled(option) {
      return this.isGloballyDisabled || option.disabled === true;
    },
    initializeSelection() {
      const selectedIndex = this.options.findIndex((option) => option.selected === true);

      if (selectedIndex !== -1) {
        this.selectedValue = selectedIndex;
      } else {
        this.selectedValue = 0;
      }
    },
  },
};
</script>

<style scoped>
.toggle-group-wrapper {
  background-color: #f6f7f9;
  border: 1px solid #ebeff4;
  border-radius: 4px;
}

.toggle-group-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

[type='radio']:checked + .toggle-group-value-container {
  background-color: white;
  transition: 180ms all ease-in-out;
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.12);
  color: #000;
}

.toggle-group-value-container {
  font-size: 0.75rem;
  border-radius: 4px;
  margin: 1px;
  text-align: center;
  color: #7f8fa4;
  cursor: pointer;
  padding: 0.5rem;
  height: 2rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.toggle-group-opacity-50 {
  opacity: 0.5;
}

/* variant="large" — matches userTypeToggle styling from _login.scss */
.toggle-group-large {
  width: 100%;
  background-color: #ebeff4;
  border-width: 4px;
  border-radius: 10px;
  padding: 6px;
}

.toggle-group-large .toggle-group-value-container {
  font-size: 1.2em;
  padding: 0.5em;
  border-radius: 10px;
  height: auto;
  margin: 0;
  color: rgba(0, 11, 32, 0.5);
  transition: 0.3s background-color, 0.3s color;
}

.toggle-group-large [type='radio']:checked + .toggle-group-value-container {
  background-color: #fff;
  color: #000b20;
  box-shadow: none;
}
</style>
